/**
 * CodexBriefButton
 *
 * Admin-only row action that builds a Codex-ready markdown brief of a
 * feedback ticket (title, severity, description, env, signed attachment
 * URLs valid for 1 hour) and copies it to the clipboard.
 */

import { useState } from 'react';
import { ClipboardCopy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  getFeedbackById,
  createFeedbackAttachmentSignedUrl
} from '@/services/feedbackService';
import type { FeedbackAttachment } from '@/types/feedback';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('CodexBriefButton', 'feedback');

interface CodexBriefButtonProps {
  feedbackId: string;
}

export function CodexBriefButton({ feedbackId }: CodexBriefButtonProps) {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      const fb = await getFeedbackById(feedbackId);
      if (!fb) {
        toast.error('Feedback not found');
        return;
      }

      const attachments = Array.isArray(fb.attachments) ? fb.attachments : [];
      const signedLinks = await Promise.all(
        attachments.map(async (att: FeedbackAttachment) => {
          const source = att.path ?? att.url;
          if (!source) {
            return null;
          }
          const url = await createFeedbackAttachmentSignedUrl(source);
          return url ? `- [${att.name}](${url})` : null;
        })
      );
      const linkLines = signedLinks.filter(Boolean).join('\n') || '_(none)_';

      const env = fb.environment ?? {};
      const md = [
        `# ${fb.type === 'bug' ? 'Bug' : 'Enhancement'}: ${fb.ticket_id} — ${fb.title}`,
        '',
        `**Severity:** ${fb.severity ?? 'n/a'}  •  **Category:** ${fb.category}  •  **Status:** ${fb.status}`,
        `**Submitted:** ${fb.created_at}  •  **URL when reported:** ${env.page_url ?? 'n/a'}`,
        '',
        '## Description',
        fb.description ?? '_(none)_',
        '',
        '## Steps to Reproduce',
        fb.steps_to_reproduce ?? '_(none)_',
        '',
        '## Expected vs Actual',
        `- Expected: ${fb.expected_behavior ?? '_(none)_'}`,
        `- Actual: ${fb.actual_behavior ?? '_(none)_'}`,
        '',
        '## Environment',
        `- Browser: ${env.browser ?? '?'} ${env.browser_version ?? ''}`.trimEnd(),
        `- OS: ${env.os ?? '?'}`,
        `- Viewport: ${env.viewport_width ?? '?'}x${env.viewport_height ?? '?'}`,
        `- Timezone: ${env.timezone ?? '?'}`,
        '',
        '## Attachments (signed URLs valid 1 hour)',
        linkLines
      ].join('\n');

      await navigator.clipboard.writeText(md);
      toast.success('Copied — paste into Codex (signed URLs valid 1 hour)');
    } catch (err) {
      log.error('Failed to build Codex brief', { feedbackId }, err as Error);
      toast.error('Failed to build Codex brief');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={busy}
      className="gap-1"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ClipboardCopy className="h-3 w-3" />}
      Codex
    </Button>
  );
}
